import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, Eye, EyeOff, ArrowLeft, AlertTriangle, XCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!username || !password) {
      setError({ message: 'Completa todos los campos' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/client/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok && response.status >= 500) {
        setError({ message: 'El servidor no está disponible en este momento. Inténtalo en unos minutos.' });
        return;
      }

      let data;
      try {
        const clonedResponse = response.clone();
        data = await clonedResponse.json();
      } catch (parseError) {
        setError({ message: 'Error al procesar la respuesta del servidor. Intenta limpiar la caché del navegador o usa una ventana de incógnito.' });
        return;
      }
      
      if (!data.success) {
        setError({
          message: data.error || 'Error de autenticación',
          remainingAttempts: data.remaining_attempts,
          locked: data.locked,
          expired: data.expired
        });
        return;
      }

      toast.success('Has entrado en tu área privada');
      sessionStorage.setItem('client', JSON.stringify(data.client));
      if (data.token) {
        sessionStorage.setItem('client_token', data.token);
      }
      navigate('/private');
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        setError({ 
          message: 'No se pudo conectar con el servidor. Comprueba tu conexión a internet.',
          hint: 'Si el problema persiste, prueba a acceder desde otro navegador o una ventana de incógnito.'
        });
      } else {
        setError({ 
          message: 'Error inesperado al iniciar sesión.',
          hint: 'Prueba a limpiar la caché del navegador, desactivar extensiones (AdBlock, VPN) o usar una ventana de incógnito.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-foreground text-background rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8" />
          </div>
          <h1 className="font-serif text-3xl mb-2">Área de Clientes</h1>
          <p className="text-muted-foreground">Jon Quintano Fotografía</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-card border border-border p-8">
          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg flex items-start gap-3 ${
                error.locked 
                  ? 'bg-red-500/10 border border-red-500/30' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}
              data-testid="login-error-message"
            >
              {error.locked ? (
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`font-medium ${error.locked ? 'text-red-500' : 'text-amber-500'}`}>
                  {error.message}
                </p>
                {error.hint && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {error.hint}
                  </p>
                )}
                {error.remainingAttempts !== undefined && error.remainingAttempts > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Te quedan <span className="font-semibold text-amber-500">{error.remainingAttempts}</span> {error.remainingAttempts === 1 ? 'intento' : 'intentos'} antes de bloquear la cuenta.
                  </p>
                )}
                {error.expired && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Contacta con el fotógrafo para renovar tu acceso.
                  </p>
                )}
              </div>
            </motion.div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              Usuario
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-10"
                placeholder="Tu nombre de usuario"
                data-testid="client-username"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="••••••••"
                data-testid="client-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-foreground text-background hover:bg-foreground/90"
            data-testid="client-submit"
          >
            {loading ? 'Accediendo...' : 'Acceder'}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Volver a la web
          </Link>
        </p>

        <p className="text-center mt-4 text-xs text-muted-foreground">
          ¿No tienes acceso? Contacta con el fotógrafo para obtener tus credenciales.
        </p>
      </motion.div>
    </div>
  );
};

export default ClientLogin;
